import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Menu,
  SegmentedControl,
  Skeleton,
  Text,
  TextInput,
  UnstyledButton,
  Slider,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { biteScopeFrom } from '../lib/play-all';
import type { Gloss, Job, Line, Problem, SavedItem, Scene } from '@nelnela/contracts';
import { itemIdsOf, marksFor, type Marks } from '../lib/marks';
import { api, ApiError, type VideoWithLines } from '../lib/api';
import { instantLookup, settle } from '../lib/instant';
import { fmtTime, formatBytes, tokenize, type Token } from '../lib/text';
import { startActivity, type ActivityHandle } from '../lib/session';
import { VocabSheet } from '../components/watch/VocabSheet';
import { createProgressWriter, resumeStartMs, type ProgressWriter } from '../lib/player/progress';
import { createYouTubePlayer, type YouTubePlayerHandle } from '../lib/player/youtube';
import { createHtml5Player, HTML5_RATES, type Html5PlayerHandle } from '../lib/player/html5';
import { useLines, usePlaybackEngine } from '../lib/player/usePlaybackEngine';
import type { EngineLine } from '../lib/player/engine';
import {
  nudgeSelection,
  resolveTap,
  selectionFromRange,
  selectionFromToken,
  selectionOffsets,
  selectionSentence,
  selectionText,
  swipeSelection,
  type PhraseSelection,
  type TokenRef,
} from '../lib/selection';
import { autoSceneName, clampSceneSpan } from '../lib/scenes';
import { isJobActive, useJob } from '../lib/useJob';
import { translationStatusOf } from '../lib/editor';
import { speak, ttsCalmLine, ttsPlayer, useTtsKeyStatus, useTtsPlayer } from '../lib/tts-player';
import { savedTitle, savedTrack } from '../lib/saved';
import { decideWordTap, savedKey, savedKeys, type ArmedTap } from '../lib/save-gesture';
import { useStudy } from '../app/study';
import { Transcript, type EnMode } from '../components/watch/Transcript';
import { RAIL_WIDTH } from '../app/layout';
import { initialEnMode, shouldPauseForSheet } from './watch-behavior';
import { useLiveProfile } from '../lib/use-profile';
import {
  GlossDrawer,
  GlossSheetContent,
  type GlossSheetState,
  type HearingState,
  type SavedState,
} from '../components/watch/GlossSheet';
import { JobPill } from '../components/watch/JobPill';
import { PatternSheet } from '../components/watch/PatternSheet';
import { ScenesBar } from '../components/watch/ScenesBar';
import { TranscriptEditor } from '../components/watch/TranscriptEditor';
import {
  EnGlyph,
  IconBack,
  IconMore,
  IconNext,
  IconPause,
  IconPlay,
  IconPrev,
  IconReplay,
} from '../components/watch/icons';

const EN_CYCLE: Record<EnMode, EnMode> = { show: 'reveal', reveal: 'hide', hide: 'show' };
const EN_LABEL: Record<EnMode, string> = {
  show: 'EN · show',
  reveal: 'EN · tap',
  hide: 'EN · off',
};
/** a panel closed this soon after opening pairs with the lookup as `lookup_dismissed` (F4) */
const DISMISS_WINDOW_MS = 1000;
/** how long a transient header line ("Saved · ▶", the AI-resting line) stays */
const NOTICE_MS = 5000;
/** long-press on the row ＋ = save with a note (F9a; the app's own timer, never the OS callout) */
const LONG_PRESS_MS = 500;
/** "Delete video" / scene delete: the armed confirm disarms after this */
const CONFIRM_MS = 3000;
const SCENES_COLLAPSED_KEY = 'nn.watch.scenesCollapsed';
/** what the speed control offers for an embed before the engine's first snapshot lands */
const YT_FALLBACK_RATES = [0.5, 0.75, 1];

interface Notice {
  text: string;
  action?: { label: string; run: () => void };
}
type JobKindArg = 'cleanup' | 'translate';
type SheetBase = Omit<Extract<GlossSheetState, { status: 'loading' }>, 'status'>;

/** the sheet identity: a new selection resets Save / Hear / alternate state */
const sheetKeyOf = (s: { lineId: string; selection: string; sentence: string } | null): string =>
  s ? `${s.lineId}\u0000${s.selection}\u0000${s.sentence}` : '';

/** `Video.transcript.runningJob` is a Pick — widen it so the pill can show before the first poll */
function jobFromPick(
  pick: NonNullable<NonNullable<VideoWithLines['transcript']>['runningJob']>,
  transcriptId: string,
): Job {
  return {
    ...pick,
    targetType: 'transcript',
    targetId: transcriptId,
    message: null,
    startedAt: null,
    finishedAt: null,
  };
}

/**
 * F4 Watch — the vertical slice: YouTube IFrame embed behind the PlayerAdapter, the ported
 * playback engine, the synced transcript, the gloss sheet, and the behavior events. Portrait:
 * player pinned 16:9 at top, ≤5 primary controls, transcript beneath, tab bar hidden (Shell).
 * Desktop: header row, player + transcript, gloss docked as a third column (keeps playing).
 *
 * M3 adds: the job pill (cleanup/translate on Workflows), the scenes bar (`i`/`o`, per-scene
 * loop), the transcript editor (`e`, owners/curators), saved lines (row ＋, sheet Save/Note),
 * "▶ Hear it" (TTS), phrase selection (tap-to-extend · swipe · ◀ ▶ nudge), `?t=` deep links,
 * export SRT / delete for private videos.
 *
 * M4c adds ONE branch: `provider === 'upload'` builds the HTML5 adapter over the Worker's range
 * proxy instead of the embed. Everything downstream is unchanged — the two handles have the same
 * shape and the engine never learns which lane it is driving.
 */
export function Watch() {
  const { videoId = '' } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // Read once, at mount: the pair is what marks the bite's tile complete, and re-reading it after
  // a navigation would re-scope an activity that is already running.
  const [biteScope] = useState(() => biteScopeFrom(searchParams));
  const queryClient = useQueryClient();
  // Read synchronously (no first-frame mobile flash / double mount of the whole tree).
  const mq = { getInitialValueInEffect: false } as const;
  const desktop = useMediaQuery('(min-width: 62em)', false, mq) ?? false;
  // A phone in landscape can't stack a full-width 16:9 player over a transcript (F4: "mobile
  // landscape: the desktop two-panel layout") — columns, but the gloss stays a sheet (D9).
  const landscapePhone =
    useMediaQuery(
      '(orientation: landscape) and (min-width: 40em) and (max-width: 62em)',
      false,
      mq,
    ) ?? false;
  const columns = desktop || landscapePhone;
  // Three columns (player · transcript · docked gloss); below this the gloss docks under the
  // player instead, so the transcript keeps its width. The threshold moved with the M5 rail:
  // the fixed furniture is 64 (rail) + 600 (player col) + 16 (gap) + 320 (gloss col) = 1000,
  // and 1312px leaves the transcript the ~310px it had at 1440 behind the old 200px rail.
  const wide = useMediaQuery('(min-width: 82em)', false, mq) ?? false;
  const study = useStudy();
  const me = study.me;

  const q = useQuery({ queryKey: ['video', videoId], queryFn: () => api.videoGet(videoId) });
  const video = q.data ?? null;
  const transcriptId = video?.transcript?.id ?? null;
  const revision = video?.transcript?.revision ?? 0;
  // The editor writes its committed lines/revision straight into the query cache (setQueryData in
  // onLinesChanged), so this one array is the truth for the transcript, the engine, and the editor.
  const lines: Line[] = useMemo(() => video?.lines ?? [], [video]);
  const linesRef = useRef(lines);
  linesRef.current = lines;
  const engineLines: EngineLine[] = useMemo(
    () => lines.map((l) => ({ id: l.id, startMs: l.startMs, endMs: l.endMs })),
    [lines],
  );
  // ------------------------------------------------ M4a: the marks (D2)
  // ONE memoryForItems call per transcript, keyed by the item ids the resolver tagged. The
  // decoration is per-viewer and changes as the fold runs, so it is its own query rather than
  // part of `videoGet` — and a transcript the resolver has not touched asks for nothing.
  const itemIds = useMemo(() => itemIdsOf(lines), [lines]);
  // Keyed on the id SET, not its size: a re-resolution that swaps one item for another keeps the
  // count identical, and a stale decoration would mark the wrong words.
  const itemsKey = useMemo(() => itemIds.join(','), [itemIds]);
  const memoryQ = useQuery({
    queryKey: ['memory', videoId, itemsKey],
    queryFn: () => api.memoryForItems(itemIds),
    enabled: itemIds.length > 0,
    staleTime: 60_000,
  });
  /**
   * M6e §3 — this video's saves, so the gloss sheet can say "Saved ✓" instead of writing a second
   * identical row, and so the double tap knows when there is nothing left to do.
   *
   * Scoped to the video and capped at one page. Deliberately NOT a flag on the gloss: a gloss row
   * is cached and memoised across learners, and saved-ness is one learner's fact.
   */
  const savedQ = useQuery({
    queryKey: ['saved', { videoId }],
    queryFn: () => api.savedList({ videoId, limit: 200 }),
    enabled: study.entitled,
    staleTime: 60_000,
  });
  const saved = useMemo(() => savedKeys(savedQ.data?.items), [savedQ.data]);
  const savedRef = useRef(saved);
  savedRef.current = saved;

  // Live, not `study.me` (a memoized boot snapshot — see lib/use-profile.ts): without this,
  // a setting changed on Me would not reach the transcript until a full reload.
  const profile = useLiveProfile(me.profiles[0] ?? null);
  const display = profile?.display ?? null;
  // Optimistic: the slider must move under the finger, not after a round trip to Frankfurt.
  const [markPrefs, setMarkPrefs] = useState<{ density: number; hidden: boolean } | null>(null);
  const markDensity = markPrefs?.density ?? display?.markDensity ?? 0.6;
  const marksHidden = markPrefs?.hidden ?? display?.marksHidden ?? false;
  // M5 settings the reading surface honours: whether a gloss pauses the video on a phone, and
  // how big the Georgian is. Both are profile-scoped, so they follow the learner to any device.
  const keepPlaying = display?.keepPlayingWhileGlossing ?? false;
  const transcriptSize = display?.transcriptSize;
  const saveMarkPrefs = useMutation({
    // ONLY the key that changed: `profileUpdate` merges `display` server-side, so sending the
    // whole object would let two in-flight writes clobber each other's field.
    mutationFn: (patch: { markDensity?: number; marksHidden?: boolean }) =>
      api.profileUpdate(profile!.id, { display: patch }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
  /** Move the marks now (local), and persist. `commit: false` while a slider is being dragged. */
  const setMarks = useCallback(
    (patch: { markDensity?: number; marksHidden?: boolean }, commit = true) => {
      setMarkPrefs((p) => ({
        density: patch.markDensity ?? p?.density ?? display?.markDensity ?? 0.6,
        hidden: patch.marksHidden ?? p?.hidden ?? display?.marksHidden ?? false,
      }));
      if (commit && profile && display) saveMarkPrefs.mutate(patch);
    },
    [display, profile, saveMarkPrefs],
  );
  const memory = memoryQ.data ?? {};
  const marks = useMemo(() => {
    const out = new Map<string, Marks>();
    for (const line of lines) {
      out.set(line.id, marksFor(line, memory, { density: markDensity, hidden: marksHidden }));
    }
    return out;
  }, [lines, memory, markDensity, marksHidden]);
  const marksRef = useRef(marks);
  marksRef.current = marks;
  const markDensityRef = useRef(markDensity);
  markDensityRef.current = markDensity;
  const marksHiddenRef = useRef(marksHidden);
  marksHiddenRef.current = marksHidden;
  /** was the token the learner just tapped wearing a mark? (set at tap, read by `lookup`) */
  const markedAtRef = useRef(false);

  // The item behind what the sheet is showing: a chunk tap names it outright; a single word finds
  // it through the resolver's tags for that line and offset.
  const [chunkItemId, setChunkItemId] = useState<string | null>(null);
  const [patternOpen, setPatternOpen] = useState(false);
  /**
   * M6a step 8: the vocabulary preview. `null` when closed; a span (or the whole episode) when
   * open. Opening it emits NOTHING — see `VocabSheet`'s own comment for why a list of words is
   * not an exposure.
   */
  const [vocabSpan, setVocabSpan] = useState<{
    startMs?: number;
    endMs?: number;
    label: string;
  } | null>(null);

  const isMine = !!video && video.visibility === 'owner' && video.ownerId === me.learner.id;
  const isCurator = me.learner.role === 'curator' || me.learner.role === 'admin';
  const canEdit = !!video && (isMine || isCurator);

  // ------------------------------------------------ player + engine
  // The player lives in a persistent host element that we own outright and mount into whatever
  // box the current layout renders (a callback ref, so a mobile↔desktop switch re-homes it
  // instead of losing it). YT.Player replaces the element it is given, so it gets a throwaway
  // inner div inside the host, never a React-managed node (see createYouTubePlayer's JSDoc).
  const [host] = useState(() => {
    const d = document.createElement('div');
    d.style.cssText = 'position:absolute;inset:0';
    return d;
  });
  const [boxEl, setBoxEl] = useState<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!boxEl) return;
    boxEl.appendChild(host);
    return () => {
      if (host.parentNode === boxEl) boxEl.removeChild(host);
    };
  }, [boxEl, host]);
  // Two adapters, one handle: `createHtml5Player` mirrors `createYouTubePlayer`'s shape
  // (adapter · ready · destroy · raw) precisely so everything below this line — engine,
  // transcript, scenes, editor, saved words, phrase selection, events — stays adapter-agnostic.
  const [handle, setHandle] = useState<YouTubePlayerHandle | Html5PlayerHandle | null>(null);
  const [playerError, setPlayerError] = useState<number | null>(null);
  const externalId = video?.externalId ?? null;
  // M4c (ADR-0006): for `provider === 'upload'` the external id is the media_object id and the
  // source is the Worker's owner-only range proxy — never a public URL, never an embed.
  const provider = video?.provider ?? null;
  const isUpload = provider === 'upload';
  // The <video> adapter puts this on the lock screen (background playback is the one thing this
  // lane gives that an embed cannot). Unused by the YouTube path.
  const videoTitle = video?.title ?? null;
  // `?t=<ms>` deep link (saved-item source links, the bite's watch tile): read once, at mount.
  const [tParamMs] = useState(() => {
    const t = Number(searchParams.get('t'));
    return Number.isFinite(t) && t > 0 ? Math.round(t) : 0;
  });
  /**
   * Where the player is CUED — `playerVars.start` for YouTube, `pendingStartMs` for an upload.
   * Never a seek: `engine.seekTo` calls `player.seekTo(sec, true)`, which the IFrame API STARTS
   * PLAYBACK from when the player is cued, so resuming through it would autoplay every time
   * Watch opened. The player is a guest (F4 principle 8); it plays when the learner says so.
   *
   * Latched, and null until it is known. `?t=` is available at mount but `video.progress` is not
   * — it arrives with the video query — and `startMs` is in the construction effect's dependency
   * list, so an unlatched value would rebuild the whole player every time progress changed. The
   * construction effect below waits for this rather than building at 0 and correcting.
   */
  const [startMs, setStartMs] = useState<number | null>(null);
  useEffect(() => {
    if (startMs !== null || !video) return;
    setStartMs(resumeStartMs(tParamMs, video.progress, video.durationMs));
  }, [video, startMs, tParamMs]);
  useEffect(() => {
    // `startMs === null` means the resume decision has not been made yet. Building now would cue
    // at 0 and then rebuild the player one render later, which on YouTube is a visible flash.
    if (!externalId || !provider || startMs === null) return;
    const inner = document.createElement('div');
    host.appendChild(inner);
    setPlayerError(null);
    // `provider` is a dependency, not a read-once: navigating from a YouTube video to an upload
    // (or back) keeps the same host element, so the player has to be torn down and rebuilt with
    // the other adapter. `videoTitle` only changes when the video row itself does.
    const h =
      provider === 'upload'
        ? createHtml5Player(inner, api.mediaStreamUrl(externalId), {
            startMs,
            onError: setPlayerError,
            ...(videoTitle ? { title: videoTitle } : {}),
          })
        : createYouTubePlayer(inner, externalId, { onError: setPlayerError, startMs });
    setHandle(h);
    return () => {
      h.destroy();
      setHandle(null);
      if (inner.parentNode === host) host.removeChild(inner);
    };
  }, [externalId, provider, videoTitle, host, startMs]);
  const { engine, state } = usePlaybackEngine(handle?.adapter ?? null);
  useLines(engine, engineLines);

  /**
   * M6a step 7: keep `video_progress` current while the learner watches, and accumulate the
   * PLAYING time the bite's watch tile shows as progress.
   *
   * Held in a ref as well as created in the effect because `activityEnd` needs `playedMs` at
   * unmount, and the effect that ends the activity must not depend on the engine (it is keyed on
   * the video, and re-running it would start a second activity every time the player rebuilt).
   */
  const progressRef = useRef<ProgressWriter | null>(null);
  useEffect(() => {
    if (!engine || !videoId) return;
    const w = createProgressWriter(engine, videoId, { put: api.videoProgressPut });
    progressRef.current = w;
    return () => {
      // The last position before the player goes away — not keepalive, because this is an
      // ordinary in-app navigation and the request has as long as it needs.
      w.flush();
      w.stop();
      if (progressRef.current === w) progressRef.current = null;
    };
  }, [engine, videoId]);

  const activeIdx = state?.activeIdx ?? -1;
  const playing = state?.playing ?? false;

  // After any player state change the iframe holds focus; take it back so the desktop keyboard
  // shortcuts keep working (F4). Never touches the player itself.
  //
  // An upload's <video> is not an HTMLIFrameElement, so this never fires for it — and must not:
  // blurring it would take away the native controls' own keyboard, and the shortcut handler
  // below already calls preventDefault() on exactly the keys a focused <video> would also act on
  // (space, ←/→), so clicking the native controls cannot double-toggle playback.
  const playerState = state?.playerState;
  useEffect(() => {
    if (!playerState) return;
    const el = document.activeElement;
    if (el instanceof HTMLIFrameElement) el.blur();
  }, [playerState]);

  // ------------------------------------------------ activity lifecycle
  const activityRef = useRef<ActivityHandle | null>(null);
  useEffect(() => {
    if (!videoId) return;
    let cancelled = false;
    let h: ActivityHandle | null = null;
    startActivity(study.session, 'watch', { videoId }, biteScope)
      .then((a) => {
        if (cancelled) {
          void a.end();
          return;
        }
        h = a;
        activityRef.current = a;
      })
      .catch((e) => console.warn('[watch] activityStart failed; events carry activityId null', e));
    return () => {
      cancelled = true;
      activityRef.current = null;
      // Leaving Watch ends the activity (04-flows lifecycle) — and pauses the guest player,
      // which the unmount of the iframe does by construction. `playedMs` is what the watch
      // tile's progress bar reads; `duration_ms` (wall clock, server-computed) is what the goal
      // ring counts. Two different questions.
      if (h) void h.end({ playedMs: progressRef.current?.playedMs() ?? 0 });
    };
  }, [videoId, study.session]);

  // Tab close / navigation away from the app: end the activity with a keepalive request (the
  // queue flushes on the same event; the server closes anything that never hears the end).
  useEffect(() => {
    const onHide = () => {
      // Both writes race the tab closing, so both are keepalive. The position matters most: an
      // activity the server never hears the end of it closes by itself, but a lost position is
      // simply lost, and it is the one the learner will notice tomorrow.
      progressRef.current?.flush({ keepalive: true });
      void activityRef.current?.end({
        playedMs: progressRef.current?.playedMs() ?? 0,
        keepalive: true,
      });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  const emit = study.emit;
  const ctxBase = useCallback(
    () => ({ activityId: activityRef.current?.id ?? null, activityType: 'watch' as const }),
    [],
  );

  // ------------------------------------------------ transient header line
  const [notice, setNotice] = useState<Notice | null>(null);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(t);
  }, [notice]);
  const say = useCallback(
    (text: string, action?: Notice['action']) => setNotice({ text, action }),
    [],
  );

  /**
   * M6a step 7: say so when we resumed. Silently opening somewhere other than the start is
   * surprising, and the learner has no other way to tell a resume from a video that simply
   * begins in the middle of a scene.
   *
   * The copy is new — design `11c`'s "resume bar" frame is the GLOBAL post-Watch link back
   * ("Resume შუადღე at 12:34" + ✕, 04-flows "Global surfaces"), not an in-Watch confirmation —
   * so this borrows that frame's idiom and uses the header's own transient line, which already
   * is a thin bar with one optional action and a dwell.
   *
   * "Start over" preserves whether the player was running: it moves the playhead, it does not
   * decide to start playing. The player is a guest.
   */
  const resumeAnnouncedRef = useRef(false);
  useEffect(() => {
    if (resumeAnnouncedRef.current) return;
    if (startMs === null || startMs === 0 || tParamMs > 0 || !engine) return;
    resumeAnnouncedRef.current = true;
    say(`Resuming from ${fmtTime(startMs)}`, {
      label: 'Start over',
      run: () => {
        const wasPlaying = engine.getState().playing;
        engine.seekTo(0);
        if (!wasPlaying) engine.pause();
      },
    });
  }, [startMs, tParamMs, engine, say]);

  // ------------------------------------------------ jobs (cleanup / translate) + the pill
  // The Add flow hands over the job `videoCreate` started (router state) — including one that
  // failed to start (`status:'error'`), which videoGet's runningJob never carries: the pill
  // shows "failed · Retry" instead of silently dropping the requested cleanup.
  const location = useLocation();
  const handedJob = (location.state as { job?: Job | null } | null)?.job ?? null;
  const [jobId, setJobId] = useState<string | null>(() =>
    handedJob && handedJob.status !== 'inert' ? handedJob.id : null,
  );
  /** the Job as the server handed it to us (start response / 409 body / videoGet's runningJob) —
   *  shows in the pill until the poller's first answer */
  const [seedJob, setSeedJob] = useState<Job | null>(() =>
    handedJob && handedJob.status !== 'inert' ? handedJob : null,
  );
  const jobIdRef = useRef(jobId);
  jobIdRef.current = jobId;
  useEffect(() => {
    if (!handedJob) return;
    // Consumed once: a reload must not resurrect a failed job that was retried since.
    nav(
      { pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: null },
    );
  }, [handedJob, location.pathname, location.search, location.hash, nav]);
  const runningPick = video?.transcript?.runningJob ?? null;
  const seededIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!runningPick || !transcriptId || !isJobActive(runningPick)) return;
    if (seededIdRef.current === runningPick.id) return;
    seededIdRef.current = runningPick.id;
    if (jobIdRef.current === runningPick.id) return; // we started it ourselves; the poller has it
    setSeedJob(jobFromPick(runningPick, transcriptId));
    setJobId(runningPick.id);
  }, [runningPick, transcriptId]);
  const invalidateVideo = useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['video', videoId] }),
    [queryClient, videoId],
  );
  const onJobDone = useCallback(() => {
    // lines/translations changed server-side: the engine follows the new `lines` array
    void invalidateVideo();
  }, [invalidateVideo]);
  const poll = useJob(jobId, { onDone: onJobDone });
  const job: Job | null = poll.job ?? (seedJob && seedJob.id === jobId ? seedJob : null);
  const dismissJob = useCallback(() => {
    setJobId(null);
    setSeedJob(null);
  }, []);

  const startJob = useCallback(
    async (kind: JobKindArg) => {
      if (!transcriptId) return;
      try {
        const j =
          kind === 'cleanup'
            ? await api.transcriptCleanup(transcriptId)
            : await api.transcriptTranslate(transcriptId);
        if (j.status === 'inert') {
          say(j.message ? `Already translated (${j.message}).` : 'Already translated.');
          return;
        }
        setSeedJob(j);
        setJobId(j.id);
      } catch (e) {
        if (e instanceof ApiError) {
          const p = e.problem as Problem & { job?: Job };
          if (p.status === 409 && p.job && typeof p.job === 'object') {
            // already running (maybe started elsewhere): follow that one
            setSeedJob(p.job);
            setJobId(p.job.id);
            return;
          }
          if (p.status === 429) {
            say(p.detail || 'The AI helper is resting for a bit — cached glosses still work.');
            return;
          }
          if (p.status === 403) {
            say(
              p.detail ||
                (kind === 'translate'
                  ? 'Translating is part of the paid layer.'
                  : 'Only the owner can clean up this transcript.'),
            );
            return;
          }
          say(p.detail || 'Couldn’t start that job right now.');
          return;
        }
        say('Couldn’t start that job right now.');
      }
    },
    [transcriptId, say],
  );
  const retryJob = useCallback(() => {
    void startJob(job?.kind === 'translate' ? 'translate' : 'cleanup');
  }, [job?.kind, startJob]);
  // Retry only when this learner may restart the job: cleanup needs edit rights (a shared
  // video's transcript is the curators'), translate needs the paid layer — otherwise the pill
  // would advertise an action that can only 403.
  const canRetryJob = !!job && (job.kind === 'translate' ? study.entitled : canEdit);

  // ------------------------------------------------ EN mode + reveal
  // M5: the opening mode is the learner's own setting (Me → English translations), not a
  // hardcoded default. A transcript with no translations at all still forces EN off — that is a
  // fact about this video — and the preference comes back when a translate job lands.
  const [enMode, setEnMode] = useState<EnMode>(display?.enVisibility ?? 'reveal');
  const enTouchedRef = useRef(false);
  const translationStatus = video?.transcript?.translationStatus;
  useEffect(() => {
    if (!translationStatus || enTouchedRef.current) return;
    setEnMode((m) => initialEnMode(display?.enVisibility, translationStatus, false, m));
  }, [translationStatus, display?.enVisibility]);
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => setRevealed(new Set()), [enMode]);
  const lineActiveSinceRef = useRef<number>(Date.now());
  useEffect(() => {
    lineActiveSinceRef.current = Date.now();
  }, [activeIdx]);
  const onReveal = useCallback(
    (line: Line) => {
      setRevealed((r) => new Set(r).add(line.id));
      // dwell = time since THIS line became active; a reveal on some other (scrolled-to) line
      // has no dwell to report
      const isActive = engine ? lines[engine.getState().activeIdx]?.id === line.id : false;
      emit({
        ...ctxBase(),
        eventType: 'reveal',
        modality: 'read',
        ...(isActive ? { latencyMs: Math.max(0, Date.now() - lineActiveSinceRef.current) } : {}),
        context: { videoId, lineId: line.id },
      });
    },
    [emit, ctxBase, videoId, engine, lines],
  );

  // ------------------------------------------------ exposure events
  // `exposure` per token per line as it plays with the learner present (F4): two rows per token
  // (`listen`, and `read` with support.translationVisible) since the transcript is always shown
  // in M2. Emitted once per line activation; a seek back (>1.5 s) re-arms so replays count.
  const exposedRef = useRef<string | null>(null);
  const lastMsRef = useRef(0);
  useEffect(() => {
    if (!state) return;
    if (state.currentMs < lastMsRef.current - 1500) exposedRef.current = null;
    lastMsRef.current = state.currentMs;
    if (!state.playing || state.activeIdx === -1) return;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    const line = lines[state.activeIdx];
    if (!line || exposedRef.current === line.id) return;
    exposedRef.current = line.id;
    const translationVisible = !!line.translation && (enMode === 'show' || revealed.has(line.id));
    const base = ctxBase();
    for (const t of tokenize(line.text)) {
      if (!t.word) continue;
      const context = { videoId, lineId: line.id, charStart: t.charStart, charEnd: t.charEnd };
      emit({ ...base, eventType: 'exposure', surface: t.word, modality: 'listen', context });
      emit({
        ...base,
        eventType: 'exposure',
        surface: t.word,
        modality: 'read',
        support: { translationVisible },
        context,
      });
    }
  }, [state, lines, enMode, revealed, emit, ctxBase, videoId]);

  // ------------------------------------------------ gloss sheet + phrase selection
  const [sheet, setSheet] = useState<GlossSheetState | null>(null);
  const sheetRef = useRef(sheet);
  sheetRef.current = sheet;
  const [sheetOpen, setSheetOpen] = useState(false);
  /** the selected span (principle 6): a single tapped word is a 'tap'-mode span of one token */
  const [phrase, setPhrase] = useState<PhraseSelection | null>(null);
  const phraseRef = useRef(phrase);
  phraseRef.current = phrase;
  const glossTokenRef = useRef(0);
  /** per-session memo of glosses already fetched (selection + sentence → gloss): re-taps are instant */
  const glossMemoRef = useRef(new Map<string, Gloss>());
  const pausedBySheetRef = useRef(false);
  const sheetOpenedAtRef = useRef(0);
  const [, force] = useState(0);
  // per-selection action state (reset whenever the sheet's selection changes)
  const [savedState, setSavedState] = useState<SavedState>('idle');
  const [actionLine, setActionLine] = useState<string | null>(null);
  const [alternate, setAlternate] = useState(false);
  /** the item the sheet's Save produced, so "Saved · ▶" plays its cached clip (no vendor call) */
  const sheetSavedRef = useRef<{ key: string; item: SavedItem } | null>(null);
  /**
   * M6e §4 — the word this surface has armed. Set by a plain single-word tap and cleared by
   * anything that changed WHAT the sheet is showing (a phrase, a chunk, a swipe, a nudge), so the
   * gesture can never save something other than the word under the finger.
   */
  const armedRef = useRef<ArmedTap | null>(null);
  /** Saved in THIS session, before the refetch lands — see `saveFromSheet`. */
  const justSavedRef = useRef(new Set<string>());

  const openSheet = useCallback(() => {
    sheetOpenedAtRef.current = Date.now();
    setSheetOpen(true);
    // D9: on mobile, opening pauses playback; the sheet carries ▶ Resume. Desktop keeps playing,
    // and from M5 so does a phone whose owner asked it to (Me → keep playing while I read glosses).
    if (engine && shouldPauseForSheet(desktop, keepPlaying, engine.getState().playing)) {
      engine.pause();
      pausedBySheetRef.current = true;
      force((n) => n + 1);
    }
  }, [desktop, keepPlaying, engine]);

  const closeSheet = useCallback(() => {
    const s = sheet;
    // Only a real lookup can be dismissed: unentitled/error sheets wrote no `lookup` row.
    if (
      s &&
      (s.status === 'done' || s.status === 'loading') &&
      Date.now() - sheetOpenedAtRef.current < DISMISS_WINDOW_MS
    ) {
      emit({
        ...ctxBase(),
        eventType: 'lookup_dismissed',
        surface: s.selection,
        modality: 'read',
        // The dwell, and the gloss it dismisses. The grader pairs on `glossId` FIRST because the
        // lookup event is written server-side after the model answers — its `ts_client` is
        // response time, not tap time, so a time-window pairing misses every mis-tap on a
        // cache-miss gloss (M4a review finding).
        latencyMs: Math.max(0, Date.now() - sheetOpenedAtRef.current),
        context: {
          videoId,
          lineId: s.lineId,
          ...(s.status === 'done' ? { glossId: s.gloss.id } : {}),
        },
      });
    }
    setSheetOpen(false);
    setPhrase(null);
    setChunkItemId(null);
    // Docked panel: clear to the placeholder. Sheet: keep the content through the slide-down
    // (the next tap replaces it) instead of flashing the placeholder mid-animation.
    if (desktop) setSheet(null);
    if (pausedBySheetRef.current) {
      pausedBySheetRef.current = false;
      engine?.play(); // restore what the sheet interrupted (F4)
    }
  }, [sheet, emit, ctxBase, videoId, desktop, engine]);

  // The iframe stays uncovered, so the learner may resume with YouTube's own ▶ while the sheet
  // is open — that counts as their decision: stop treating the pause as ours.
  useEffect(() => {
    if (playing && pausedBySheetRef.current) {
      pausedBySheetRef.current = false;
      force((n) => n + 1);
    }
  }, [playing]);

  const onResume = useCallback(() => {
    pausedBySheetRef.current = false;
    engine?.play();
    force((n) => n + 1);
  }, [engine]);

  /**
   * Gloss a span (single word or phrase): highlight it, open the sheet instantly (memo or
   * skeleton), and call `gloss` — every call is a `lookup` event written server-side, with
   * `support.selection` = how the span was formed.
   */
  const lookup = useCallback(
    (sel: PhraseSelection, now = Date.now()) => {
      const ls = linesRef.current;
      const selection = selectionText(sel, ls);
      const sentence = selectionSentence(sel, ls);
      if (!selection || !sentence) return;
      const offsets = selectionOffsets(sel, ls);
      const line = ls.find((l) => l.id === sel.startLineId);
      const base: SheetBase = {
        selection,
        sentence,
        lineId: sel.startLineId,
        ...(offsets ? { sel: offsets } : {}),
        // a plain tap keeps the M2 single-word header; anything else shows the span header with
        // the ◀ ▶ handles (also when a nudge narrowed it back to one word)
        phrase: sel.mode === 'tap' ? null : sel,
        mode: sel.mode,
      };
      // M6e §4: only a plain single-word tap arms the save gesture. A swipe, a nudge, an extend
      // and a multi-token chunk all arrive here too, and all of them mean the sheet is showing
      // something OTHER than the word a finger last landed on — so they disarm it.
      armedRef.current =
        sel.mode === 'tap' ? { key: savedKey(selection, sentence), at: now } : null;
      setPhrase(sel);
      if (sheetKeyOf(base) !== sheetKeyOf(sheetRef.current)) {
        setSavedState('idle');
        setActionLine(null);
        setAlternate(false);
      }
      if (!study.entitled) {
        setSheet({ status: 'unentitled', ...base });
        openSheet();
        return;
      }
      const tk = ++glossTokenRef.current;
      const memoKey = `${selection}\u0000${sentence}`;
      const memo = glossMemoRef.current.get(memoKey);
      // Seen this exact selection-in-sentence this session: show it instantly. The server call
      // still goes out — every tap is a `lookup` event, written server-side — but the sheet
      // doesn't wait.
      setSheet(memo ? { status: 'done', ...base, gloss: memo } : { status: 'loading', ...base });
      openSheet();
      // M10-i i-1: the instant layer, in flight beside the gloss rather than before it. Nothing
      // waits on it — the sheet is already open — and it lands in ~1 round trip against the
      // gloss's ~5 s, so the skeleton usually never appears bare. The same `glossTokenRef` guards
      // the race, so a second tap discards the first tap's instant answer too.
      // `settle` against `memo`, not against nothing: on a session-memo hit the gloss request
      // below short-circuits (`|| memo`), so this handler is the ONLY one that runs — and an
      // unreconciled block under an already-shown meaning is the mixed sheet reconcile exists for.
      instantLookup(selection, sentence)
        .then((inst) => settle(inst, memo))
        .then((inst) => {
          if (glossTokenRef.current !== tk) return;
          setSheet((prev) =>
            prev && sheetKeyOf(prev) === sheetKeyOf(base) ? { ...prev, instant: inst } : prev,
          );
        });
      const translationVisible =
        !!line?.translation && (enMode === 'show' || revealed.has(line.id));
      api
        .gloss({
          targetLang: 'ka',
          baseLang: 'en',
          selection,
          sentence,
          context: {
            sessionId: study.session.id,
            ...(activityRef.current ? { activityId: activityRef.current.id } : {}),
            lineId: sel.startLineId,
            translationVisible,
            selectionMode: sel.mode,
            // Every tap AND every non-tap records what the surface was advertising at that moment
            // (spec § 2) — without it the model cannot tell "looked it up unprompted" from
            // "looked it up because we dotted it".
            marked: markedAtRef.current,
            markThreshold: markDensityRef.current,
            marksHidden: marksHiddenRef.current,
          },
        })
        .then(async (gloss) => {
          glossMemoRef.current.set(memoKey, gloss);
          if (glossTokenRef.current !== tk || memo) return;
          // The provisional block's dictionary facts survive the gloss — unless the model
          // disagrees about the LEMMA, in which case they were about the wrong word and the whole
          // block is re-resolved. Half a sheet about lemma X under a meaning for lemma Y is worse
          // than a late repaint.
          const settled = await settle(await instantLookup(selection, sentence), gloss);
          if (glossTokenRef.current !== tk) return;
          setSheet({ status: 'done', ...base, instant: settled, gloss });
        })
        .catch((e: unknown) => {
          if (glossTokenRef.current !== tk || memo) return;
          if (e instanceof ApiError && e.problem.status === 403) {
            setSheet({ status: 'unentitled', ...base });
            return;
          }
          setSheet({ status: 'error', ...base, message: glossErrorMessage(e) });
        });
    },
    [study.entitled, study.session.id, openSheet, enMode, revealed],
  );

  // ------------------------------------------------ save (F9a)
  // Above the tap handlers on purpose (M6e §4): `onWordTap` names `saveFromSheet` in its
  // dependency array, which is evaluated during render — a `const` declared further down the
  // component would be in the temporal dead zone every time this screen renders.
  const playSaved = useCallback(
    (item: SavedItem, key?: string) => {
      const track = savedTrack(item);
      if (track) {
        void ttsPlayer.play(key ? { ...track, key } : track);
        return;
      }
      speak(item.sentence, { title: savedTitle(item), subtitle: 'saved · TTS', key }).catch(
        (e: unknown) => say(ttsCalmLine(e)),
      );
    },
    [say],
  );

  /** `savedCreate` — the server writes the `capture` event and synthesizes the sentence. */
  const doSave = useCallback(
    async (
      body: {
        selection?: string;
        sentence: string;
        translation?: string | null;
        lineId?: string;
        glossId?: string;
        note?: string;
      },
      opts: { toast?: boolean } = {},
    ): Promise<SavedItem | null> => {
      if (!study.entitled) {
        say('Saving is part of the paid layer.');
        return null;
      }
      try {
        const item = await api.savedCreate({
          targetLang: 'ka',
          baseLang: 'en',
          ...(body.selection ? { selection: body.selection } : {}),
          sentence: body.sentence,
          ...(body.translation ? { translation: body.translation } : {}),
          ...(body.glossId ? { glossId: body.glossId } : {}),
          ...(body.lineId ? { lineId: body.lineId } : {}),
          videoId,
          ...(body.note ? { note: body.note } : {}),
          synthesizeAudio: true,
        });
        void queryClient.invalidateQueries({ queryKey: ['saved'] });
        if (opts.toast !== false)
          say('Saved ·', { label: '▶', run: () => playSaved(item, `saved:${item.id}`) });
        return item;
      } catch (e) {
        if (e instanceof ApiError && e.problem.status === 403)
          say('Saving is part of the paid layer.');
        else say('Couldn’t save this — try again in a moment.');
        return null;
      }
    },
    [study.entitled, videoId, queryClient, say, playSaved],
  );

  /** the joined translation of the lines a sheet selection spans (all present) — else nothing */
  const sheetTranslation = useCallback((s: GlossSheetState): string | undefined => {
    const ls = linesRef.current;
    const ids = s.phrase ? [s.phrase.startLineId, s.phrase.endLineId] : [s.lineId];
    const si = ls.findIndex((l) => l.id === ids[0]);
    const ei = ls.findIndex((l) => l.id === ids[ids.length - 1]);
    if (si === -1 || ei === -1 || ei < si) return undefined;
    const parts = ls.slice(si, ei + 1).map((l) => l.translation);
    return parts.every((p): p is string => !!p) ? parts.join(' ') : undefined;
  }, []);

  const saveFromSheet = useCallback(
    (note?: string) => {
      const s = sheetRef.current;
      if (!s) return;
      const key = sheetKeyOf(s);
      setSavedState('saving');
      void doSave(
        {
          selection: s.selection,
          sentence: s.sentence,
          translation: sheetTranslation(s),
          lineId: s.lineId,
          ...(s.status === 'done' ? { glossId: s.gloss.id } : {}),
          ...(note ? { note } : {}),
        },
        { toast: false },
      ).then((item) => {
        if (item) {
          // Recorded locally as well as invalidated (M6e §3): `savedCreate` writes a row every
          // time it is called, and between the POST resolving and the refetch landing the scoped
          // set still says this word is unsaved — long enough for an impatient second double-tap
          // to buy a duplicate. Recorded before the sheet check, because the row exists either way.
          justSavedRef.current.add(savedKey(s.selection, s.sentence));
        }
        if (sheetKeyOf(sheetRef.current) !== key) return; // the sheet moved on meanwhile
        if (item) {
          sheetSavedRef.current = { key, item };
          setSavedState('saved');
        } else setSavedState('error');
      });
    },
    [doSave, sheetTranslation],
  );
  const onSheetSave = useCallback(() => saveFromSheet(), [saveFromSheet]);
  const onSheetNote = useCallback((text: string) => saveFromSheet(text), [saveFromSheet]);

  /**
   * A word tap: with a span open on this/an adjacent line it extends to cover the tapped word
   * (tap-to-extend); otherwise (nothing open, tap inside the span, line out of reach) it is a
   * fresh single-word lookup; over the 8-word cap it is ignored.
   *
   * M6e §4 adds ONE case in front of all of that: a second tap on the SAME word, inside
   * `SAVE_TAP_WINDOW_MS`, saves it and opens nothing further. What that replaces is the tap that
   * used to re-open a memoised gloss of a word already on screen — the only tap on this surface
   * that did nothing new. **A tap on a different word is untouched**: `decideWordTap` compares
   * keys before anything else, so tap-to-extend still sees exactly the taps it saw before, which
   * is the one thing this gesture was not allowed to cost.
   *
   * ⟂ Spec departure, recorded not edited: `docs/design/reading-surface-spec.md` says saving is a
   * row-＋ affordance, not a text gesture. The spec is protected and is not being edited — the
   * citation is ROADMAP § M6e §4. The ＋ still works and still means the same thing.
   *
   * The two timers this gesture sits between are `SWIPE_CLICK_GUARD_MS` and
   * `SAVE_TAP_WINDOW_MS`, and they live in one file (`lib/save-gesture.ts`) so they cannot drift:
   * a click inside the swipe guard never reaches this handler at all, which is exactly why a
   * swipe that ends on the armed word cannot silently save it.
   */
  const onWordTap = useCallback(
    (token: Token, line: Line, tokIdx: number) => {
      const now = Date.now();
      const tap: ArmedTap = { key: savedKey(token.word, line.text), at: now };
      // `entitled` first: without it a second tap by an unentitled learner would take the save
      // branch, be refused inside `doSave`, and leave the sheet in an error state instead of
      // simply re-opening the gloss it already had.
      if (
        study.entitled &&
        decideWordTap(armedRef.current, tap, {
          alreadySaved: savedRef.current.has(tap.key) || justSavedRef.current.has(tap.key),
        }) === 'save'
      ) {
        armedRef.current = null; // a third tap is not a second save
        saveFromSheet();
        return;
      }
      // A plain word tap is never about the chunk that was open before it — clearing this is what
      // keeps the glyph, the grammar strip and "I already know this" pointing at the tapped word.
      setChunkItemId(null);
      markedAtRef.current = marksRef.current.get(line.id)?.weak.has(tokIdx) === true;
      const r = resolveTap(phraseRef.current, { line, token }, linesRef.current);
      if (r.kind === 'ignore') return;
      lookup(r.kind === 'extend' ? r.selection : selectionFromToken(line, token), now);
    },
    [lookup, saveFromSheet, study.entitled],
  );

  /**
   * A tap inside a chunk (a multi-token item the resolver tagged) opens the CHUNK first — one
   * gloss of the whole span, with the words/phrase segment to drill into it (spec § 3). The span
   * travels as a phrase selection so the ◀ ▶ handles and the highlight work unchanged; the item
   * id rides alongside so the sheet can show its glyph and grammar strip.
   */
  const onChunkTap = useCallback(
    (itemId: string, line: Line, charStart: number, charEnd: number) => {
      markedAtRef.current = true; // a chunk is always a mark
      setChunkItemId(itemId);
      const sel = selectionFromRange(line, charStart, charEnd);
      if (sel) lookup(sel);
    },
    [lookup],
  );

  // Swipe across tokens: live highlight while dragging, one gloss when the finger lifts (the
  // mobile Drawer must not open mid-drag — its overlay would swallow the pointer).
  const swipeRef = useRef<PhraseSelection | null>(null);
  const onSwipeStart = useCallback(() => {
    swipeRef.current = null;
    setChunkItemId(null);
    markedAtRef.current = false; // a swipe is not a tap on a marked token
    if (engine && shouldPauseForSheet(desktop, keepPlaying, engine.getState().playing)) {
      engine.pause();
      pausedBySheetRef.current = true;
      force((n) => n + 1);
    }
  }, [desktop, keepPlaying, engine]);
  const onSwipe = useCallback((a: TokenRef, b: TokenRef) => {
    const sel = swipeSelection(a, b, linesRef.current);
    if (!sel) return; // over the cap / not a word: keep the last valid span
    swipeRef.current = sel;
    setPhrase(sel);
  }, []);
  const onSwipeEnd = useCallback(() => {
    const sel = swipeRef.current;
    swipeRef.current = null;
    if (sel) {
      lookup(sel);
      return;
    }
    // a swipe that never landed on a word: undo the pause we made for it (no sheet to resume from)
    if (pausedBySheetRef.current && !sheetOpen) {
      pausedBySheetRef.current = false;
      engine?.play();
    }
  }, [lookup, sheetOpen, engine]);

  const onNudge = useCallback(
    (side: 'start' | 'end', dir: -1 | 1) => {
      const cur = phraseRef.current;
      if (!cur) return;
      const next = nudgeSelection(cur, side, dir, linesRef.current);
      if (next) lookup(next);
    },
    [lookup],
  );

  // ------------------------------------------------ hear (TTS) + alternate

  const hearKey = sheet ? `gloss:${sheetKeyOf(sheet)}` : 'gloss:';
  const hearStatus = useTtsKeyStatus(hearKey);
  const hearing: HearingState =
    hearStatus === 'loading' ? 'loading' : hearStatus === 'playing' ? 'playing' : 'idle';
  const onHear = useCallback(() => {
    const s = sheetRef.current;
    if (!s) return;
    const key = `gloss:${sheetKeyOf(s)}`;
    const st = ttsPlayer.getState();
    if (st.track?.key === key && st.status === 'playing') {
      ttsPlayer.pause();
      return;
    }
    if (st.track?.key === key && st.status === 'paused') {
      ttsPlayer.resume();
      return;
    }
    setActionLine(null);
    // the sheet's own save produced audio for this very sentence: play the cached clip
    const saved = sheetSavedRef.current;
    if (saved && saved.key === sheetKeyOf(s) && saved.item.audio) {
      playSaved(saved.item, key);
      return;
    }
    speak(s.sentence, { title: s.selection || s.sentence, subtitle: 'gloss · TTS', key }).catch(
      (e: unknown) => setActionLine(ttsCalmLine(e)),
    );
  }, [playSaved]);

  const onAlternate = useCallback(() => {
    const s = sheetRef.current;
    if (!s || s.status !== 'done') return;
    const { status: _status, gloss, ...base } = s;
    void _status;
    const tk = ++glossTokenRef.current;
    setActionLine(null);
    setSheet({ status: 'loading', ...base });
    api
      .glossAlternate({ glossId: gloss.id })
      .then((alt) => {
        if (glossTokenRef.current !== tk) return;
        setSheet({ status: 'done', ...base, gloss: alt });
        setAlternate(true);
      })
      .catch((e: unknown) => {
        if (glossTokenRef.current !== tk) return;
        setSheet({ status: 'done', ...base, gloss }); // keep what we had
        setActionLine(glossErrorMessage(e));
      });
  }, []);

  // ------------------------------------------------ row ＋ (save a line; long-press = with a note)
  const [noteFor, setNoteFor] = useState<Line | null>(null);
  const saveLine = useCallback(
    (line: Line, withNote: boolean) => {
      if (!study.entitled) {
        say('Saving is part of the paid layer.');
        return;
      }
      if (withNote) {
        setNoteFor(line);
        return;
      }
      void doSave({ sentence: line.text, translation: line.translation, lineId: line.id });
    },
    [study.entitled, say, doSave],
  );
  const entitled = study.entitled;
  const renderRowEnd = useCallback(
    (line: Line) => <SaveLineButton line={line} entitled={entitled} onSave={saveLine} />,
    [entitled, saveLine],
  );
  const submitLineNote = useCallback(
    (note: string) => {
      const line = noteFor;
      setNoteFor(null);
      if (!line) return;
      void doSave({ sentence: line.text, translation: line.translation, lineId: line.id, note });
    },
    [noteFor, doSave],
  );

  // ------------------------------------------------ scenes
  const [scenes, setScenes] = useState<Scene[]>([]);
  const queryScenes = video?.scenes;
  useEffect(() => {
    if (queryScenes) setScenes(queryScenes);
  }, [queryScenes]);
  const [pendingIn, setPendingIn] = useState<number | null>(null);
  const [pendingOut, setPendingOut] = useState<number | null>(null);
  const [scenesCollapsed, setScenesCollapsed] = useState<boolean>(() => {
    try {
      const s = sessionStorage.getItem(SCENES_COLLAPSED_KEY);
      if (s === '1' || s === '0') return s === '1';
    } catch {
      /* private mode */
    }
    return !desktop;
  });
  const toggleScenes = useCallback(() => {
    setScenesCollapsed((c) => {
      try {
        sessionStorage.setItem(SCENES_COLLAPSED_KEY, c ? '0' : '1');
      } catch {
        /* private mode */
      }
      return !c;
    });
  }, []);
  const markIn = useCallback(() => {
    if (!engine) return;
    setPendingIn(Math.round(engine.getState().currentMs));
  }, [engine]);
  const markOut = useCallback(() => {
    if (!engine) return;
    setPendingOut(Math.round(engine.getState().currentMs));
  }, [engine]);
  const clearPending = useCallback(() => {
    setPendingIn(null);
    setPendingOut(null);
  }, []);
  const durationMs = video?.durationMs ?? null;
  const addScene = useCallback(
    (name: string) => {
      if (pendingIn == null || pendingOut == null) return;
      const span = clampSceneSpan(pendingIn, pendingOut, durationMs);
      if (!span) {
        say('A scene needs its end after its start.');
        return;
      }
      api
        .sceneCreate({
          videoId,
          name: name.trim() || autoSceneName(span.startMs),
          startMs: span.startMs,
          endMs: span.endMs,
        })
        .then((scene) => {
          setScenes((s) => [...s, scene]);
          clearPending();
          void invalidateVideo();
        })
        .catch((e: unknown) => {
          if (e instanceof ApiError && e.problem.status === 403)
            say('Scenes are part of the paid layer.');
          else say('Couldn’t add the scene right now.');
        });
    },
    [pendingIn, pendingOut, durationMs, videoId, say, clearPending, invalidateVideo],
  );
  const renameScene = useCallback(
    (scene: Scene, name: string) => {
      setScenes((s) => s.map((x) => (x.id === scene.id ? { ...x, name } : x)));
      api
        .sceneUpdate(scene.id, { name })
        .catch(() => say('Couldn’t rename the scene right now.'))
        .finally(() => void invalidateVideo());
    },
    [say, invalidateVideo],
  );
  const deleteScene = useCallback(
    (scene: Scene) => {
      setScenes((s) => s.filter((x) => x.id !== scene.id));
      if (engine?.getState().loop?.label === scene.id) engine.setLoop(null);
      api
        .sceneDelete(scene.id)
        .catch(() => say('Couldn’t delete the scene right now.'))
        .finally(() => void invalidateVideo());
    },
    [engine, say, invalidateVideo],
  );
  const jumpScene = useCallback(
    (scene: Scene) => engine?.seekTo(scene.startMs, { play: true }),
    [engine],
  );
  const loopScene = useCallback(
    (scene: Scene) => {
      if (!engine) return;
      engine.setLoop({ startMs: scene.startMs, endMs: scene.endMs, label: scene.id });
      engine.seekTo(scene.startMs, { play: true });
      emit({
        ...ctxBase(),
        eventType: 'replay',
        modality: 'listen',
        support: { loop: true, sceneId: scene.id },
        context: { videoId, sceneId: scene.id },
      });
    },
    [engine, emit, ctxBase, videoId],
  );
  const stopLoop = useCallback(() => engine?.setLoop(null), [engine]);
  const seekMs = useCallback((ms: number) => engine?.seekTo(ms), [engine]);
  const loopLabel = state?.loop?.label ?? null;
  const loopingSceneId = loopLabel && scenes.some((s) => s.id === loopLabel) ? loopLabel : null;

  // ------------------------------------------------ editor (F5)
  const [editing, setEditing] = useState(false);
  const onLinesChanged = useCallback(
    (next: Line[], rev: number) => {
      // A refetch already in flight (invalidateVideo from a scene op / reload / job done) would
      // resolve after this write with the pre-commit lines and clobber it — cancel it first.
      void queryClient.cancelQueries({ queryKey: ['video', videoId] });
      queryClient.setQueryData<VideoWithLines>(['video', videoId], (old) =>
        old
          ? {
              ...old,
              lines: next,
              transcript: old.transcript
                ? {
                    ...old.transcript,
                    revision: rev,
                    lineCount: next.length,
                    // the mutation response carries no status; mirror the server's recompute
                    translationStatus: translationStatusOf(next),
                  }
                : old.transcript,
            }
          : old,
      );
    },
    [queryClient, videoId],
  );
  const onEditorStartJob = useCallback((kind: JobKindArg) => void startJob(kind), [startJob]);
  const onRequestReload = useCallback(() => void invalidateVideo(), [invalidateVideo]);
  const exitEditor = useCallback(() => setEditing(false), []);
  const editorSeek = useCallback((ms: number) => engine?.seekTo(ms, { play: true }), [engine]);

  // ------------------------------------------------ delete video (mine only; 2-tap)
  const [deleteArmed, setDeleteArmed] = useState(false);
  useEffect(() => {
    if (!deleteArmed) return;
    const t = setTimeout(() => setDeleteArmed(false), CONFIRM_MS);
    return () => clearTimeout(t);
  }, [deleteArmed]);
  const deleteVideo = useCallback(() => {
    if (!deleteArmed) {
      setDeleteArmed(true);
      return;
    }
    setDeleteArmed(false);
    api
      .videoDelete(videoId)
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['videos'] });
        void queryClient.invalidateQueries({ queryKey: ['saved'] });
        nav('/watch', { replace: true });
      })
      .catch(() => say('Couldn’t delete this video right now.'));
  }, [deleteArmed, videoId, queryClient, nav, say]);

  // ------------------------------------------------ commands
  const cmd = useMemo(
    () => ({
      togglePlay: () => engine?.togglePlay(),
      replay: () => {
        if (!engine) return;
        const i =
          engine.getState().activeIdx !== -1
            ? engine.getState().activeIdx
            : engine.getState().lastActiveIdx;
        engine.replayLine();
        const line = i >= 0 ? lines[i] : undefined;
        if (line)
          emit({
            ...ctxBase(),
            eventType: 'replay',
            modality: 'listen',
            context: { videoId, lineId: line.id },
          });
      },
      prev: () => engine?.stepLine(-1),
      next: () => engine?.stepLine(1),
      cycleEn: () => {
        enTouchedRef.current = true;
        setEnMode((m) => EN_CYCLE[m]);
      },
      loopLine: () => {
        if (!engine) return;
        if (engine.getState().loop) {
          engine.setLoop(null);
          return;
        }
        const st = engine.getState();
        const i = st.activeIdx !== -1 ? st.activeIdx : st.lastActiveIdx;
        engine.loopCurrentLine();
        const line = i >= 0 ? lines[i] : undefined;
        if (line)
          emit({
            ...ctxBase(),
            eventType: 'replay',
            modality: 'listen',
            support: { loop: true },
            context: { videoId, lineId: line.id },
          });
      },
      toggleAutoPause: () => engine?.toggleAutoPause(),
      escape: () => {
        // First Esc closes the sheet (the mobile Drawer handles its own Esc — don't double-close),
        // a second Esc clears the loop.
        if (sheetOpen) {
          if (desktop) closeSheet();
          return;
        }
        engine?.setLoop(null);
      },
      seekLine: (line: Line) => engine?.seekTo(line.startMs, { play: true }),
      markIn,
      markOut,
      toggleEditor: () => {
        if (!canEdit) return;
        if (!editing && sheetOpen) closeSheet(); // the editor replaces the reading surface
        setEditing(!editing);
      },
    }),
    [
      engine,
      lines,
      emit,
      ctxBase,
      videoId,
      sheetOpen,
      closeSheet,
      desktop,
      markIn,
      markOut,
      canEdit,
      editing,
    ],
  );

  // Desktop keyboard shortcuts (the strip buttons are canonical; F4). Ignored while typing.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      switch (e.key) {
        case ' ':
          cmd.togglePlay();
          e.preventDefault();
          break;
        case 'r':
          cmd.replay();
          break;
        case 'ArrowLeft':
          cmd.prev();
          e.preventDefault();
          break;
        case 'ArrowRight':
          cmd.next();
          e.preventDefault();
          break;
        case 'a':
          cmd.toggleAutoPause();
          break;
        case 'l':
          cmd.loopLine();
          break;
        case 'h':
          cmd.cycleEn();
          break;
        case 'i':
          cmd.markIn();
          break;
        case 'o':
          cmd.markOut();
          break;
        case 'e':
          cmd.toggleEditor();
          break;
        case 'Escape':
          cmd.escape();
          break;
        default:
          return;
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cmd]);

  // ------------------------------------------------ sheet geometry (mobile)
  // The sheet's max height stops at the player's bottom edge — never covers the iframe.
  const [playerBottom, setPlayerBottom] = useState(0);
  const [colEl, setColEl] = useState<HTMLDivElement | null>(null);
  const [playerColumnRight, setPlayerColumnRight] = useState(0);
  useEffect(() => {
    if (!colEl) return;
    const measure = () => setPlayerColumnRight(Math.round(colEl.getBoundingClientRect().right));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(colEl);
    return () => ro.disconnect();
  }, [colEl]);
  useEffect(() => {
    const box = boxEl;
    if (!box) return;
    const measure = () => setPlayerBottom(Math.round(box.getBoundingClientRect().bottom));
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [boxEl]);

  // ------------------------------------------------ M4a: the item behind the open sheet
  // A chunk tap names its item; a single word finds it through the resolver's tags for that line
  // and offset. Either way the sheet gets the glyph, the grammar strip and "I already know this".
  const sheetItemId = useMemo(() => {
    if (chunkItemId) return chunkItemId;
    if (!sheet?.sel) return null;
    const line = lines.find((l) => l.id === sheet.lineId);
    const inSpan = (line?.items ?? []).filter(
      (i) =>
        i.charStart !== null &&
        i.charEnd !== null &&
        i.charStart >= sheet.sel!.charStart &&
        i.charEnd <= sheet.sel!.charEnd,
    );
    // The resolver writes a lexeme AND (often) an affix pattern at identical offsets for one
    // token — "თბილისში" is both the noun and the -ში pattern. A word tap is about the word; the
    // pattern is reached by its own chunk, or from the grammar strip.
    const hit = inSpan.find((i) => i.kind === 'lexeme') ?? inSpan[0];
    return hit?.itemId ?? null;
  }, [chunkItemId, sheet, lines]);

  const itemQ = useQuery({
    queryKey: ['item', sheetItemId],
    queryFn: () => api.itemGet(sheetItemId!),
    enabled: !!sheetItemId,
    staleTime: 30_000,
  });
  const lessonQ = useQuery({
    queryKey: ['lessonForItem', sheetItemId],
    queryFn: () => api.lessonForItem(sheetItemId!),
    enabled: !!sheetItemId,
    staleTime: 5 * 60_000,
  });
  const suppress = useMutation({
    mutationFn: (on: boolean) =>
      on ? api.itemSuppress(sheetItemId!) : api.itemUnsuppress(sheetItemId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['item', sheetItemId] });
      void queryClient.invalidateQueries({ queryKey: ['memory', videoId] });
    },
  });
  const onSelfReport = useCallback(
    (correct: boolean) => {
      if (!sheetItemId) return;
      emit({
        ...ctxBase(),
        eventType: 'self_report',
        itemId: sheetItemId,
        modality: 'read',
        correct,
        context: { videoId, ...(sheet?.lineId ? { lineId: sheet.lineId } : {}) },
      });
      say(correct ? 'Noted — you know it.' : 'Noted — we’ll bring it back.');
    },
    [sheetItemId, emit, ctxBase, videoId, sheet, say],
  );
  const onLessonOpen = useCallback((lessonId: string) => void lessonId, []);
  const onLessonClose = useCallback(
    (lessonId: string, dwellMs: number) => {
      emit({
        ...ctxBase(),
        eventType: 'lesson_viewed',
        ...(sheetItemId ? { itemId: sheetItemId } : {}),
        modality: 'read',
        latencyMs: Math.max(0, Math.round(dwellMs)),
        context: { videoId, lessonId },
      });
    },
    [emit, ctxBase, videoId, sheetItemId],
  );

  // ------------------------------------------------ render
  const title = video?.title ?? (q.isPending ? '' : 'Video');
  const channelName = video?.channel?.name ?? null;
  // The control shows the adapter's own rates (`state.rates`); the fallback covers the frames
  // before the engine's first snapshot. It has to follow the provider too — an upload flashing
  // YouTube's [0.5, 0.75, 1] would hide the fine steps that are the point of this lane.
  const rates: readonly number[] = state?.rates ?? (isUpload ? HTML5_RATES : YT_FALLBACK_RATES);
  const rateOptions = rates.map((r) => ({
    value: String(r),
    label: r === 1 ? '1×' : String(r),
  }));
  // Deleting an upload frees real storage, so the second tap says how much (M4c). `mediaBytes` is
  // uploads-only and optional, so the YouTube wording is also the fallback if it is absent.
  const uploadBytes = isUpload ? (video?.mediaBytes ?? null) : null;
  const deleteLabel = !deleteArmed
    ? 'Delete video'
    : uploadBytes != null
      ? `Delete video and its ${formatBytes(uploadBytes)} file? Tap again to confirm`
      : 'Delete video? Tap again to confirm';
  const resume =
    !desktop && sheet ? { visible: pausedBySheetRef.current && !playing, onResume } : null;
  // M6e §3: a selection already in this video's saved list renders the action as spent —
  // `savedCreate` would otherwise write a second identical row — but only while nothing is in
  // flight, so a save the learner just made still reads back as "Saved · ▶".
  const sheetSaved: SavedState =
    savedState === 'idle' && sheet && saved.has(savedKey(sheet.selection, sheet.sentence))
      ? 'already'
      : savedState;
  const sheetActions = {
    entitled: study.entitled,
    onNudge,
    onSave: onSheetSave,
    savedState: sheetSaved,
    onHear,
    hearing,
    actionLine,
    onAlternate,
    alternate,
    onNote: onSheetNote,
    // M4a
    item: itemQ.data ?? null,
    lesson: lessonQ.data ?? null,
    onLessonOpen,
    onLessonClose,
    onSelfReport: sheetItemId ? onSelfReport : undefined,
    onSuppress: sheetItemId ? (on: boolean) => suppress.mutate(on) : undefined,
    onDrillToWords: chunkItemId ? () => setChunkItemId(null) : undefined,
    onSaveAsPattern: sheet ? () => setPatternOpen(true) : undefined,
  };
  const patternSheet = sheet && (
    <PatternSheet
      opened={patternOpen}
      onClose={() => setPatternOpen(false)}
      selection={sheet.selection}
      sentence={sheet.sentence}
      {...(sheet.lineId ? { lineId: sheet.lineId } : {})}
      onSaved={(m) => {
        say(m);
        void queryClient.invalidateQueries({ queryKey: ['video', videoId] });
      }}
    />
  );

  /**
   * M6a step 8: the vocabulary preview, over the whole episode or one scene.
   *
   * A row opens the item's own page rather than the gloss drawer. The brief asks for the drawer,
   * but `GlossSheetState` is built from a text SELECTION — a lineId, a sentence and character
   * offsets — and there is no way to construct one from a bare item id that would not be a
   * fabricated selection. `/words/item/:itemId` already IS the item card, with the same gloss,
   * examples and memory state, and leaving Watch is cheap now that this milestone remembers the
   * position: coming back resumes where the player was.
   */
  const vocabDrawer = videoId && (
    <VocabSheet
      opened={vocabSpan !== null}
      onClose={() => setVocabSpan(null)}
      videoId={videoId}
      label={vocabSpan?.label ?? ''}
      {...(vocabSpan?.startMs !== undefined ? { startMs: vocabSpan.startMs } : {})}
      {...(vocabSpan?.endMs !== undefined ? { endMs: vocabSpan.endMs } : {})}
      onOpenItem={(itemId) => nav(`/words/item/${itemId}`)}
    />
  );

  // The sheet (mobile, incl. landscape phone) — a bottom drawer that stops at the player's
  // bottom edge, or a right-hand drawer over the transcript column in landscape.
  const drawer = !desktop && (
    <GlossDrawer
      opened={sheetOpen && sheet !== null}
      side={landscapePhone ? 'right' : 'bottom'}
      insetPx={landscapePhone ? playerColumnRight : playerBottom}
      state={sheet}
      onClose={closeSheet}
      resume={resume}
      {...sheetActions}
    />
  );

  const strip = (
    <div
      style={{
        display: 'flex',
        alignItems: 'stretch',
        height: desktop ? 48 : 56,
        padding: '2px 8px',
        gap: 2,
        flexShrink: 0,
      }}
    >
      <StripButton label={playing ? 'pause' : 'play'} onClick={cmd.togglePlay}>
        {playing ? <IconPause /> : <IconPlay />}
      </StripButton>
      <StripButton label="replay" onClick={cmd.replay}>
        <IconReplay />
      </StripButton>
      <StripButton label="prev" onClick={cmd.prev}>
        <IconPrev />
      </StripButton>
      <StripButton label="next" onClick={cmd.next}>
        <IconNext />
      </StripButton>
      <StripButton label={EN_LABEL[enMode]} onClick={cmd.cycleEn}>
        <EnGlyph mode={enMode} />
      </StripButton>
    </div>
  );

  // The header's status slot: the job pill (cleanup/translate) or a transient line ("Saved · ▶",
  // the AI-resting line). Sits between the title and the overflow; the title truncates first.
  const headerStatus = notice ? (
    <Text
      fz={11}
      c="dimmed"
      role="status"
      style={{
        flexShrink: 1,
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {notice.text}
      {notice.action && (
        <>
          {' '}
          <UnstyledButton
            onClick={notice.action.run}
            aria-label={notice.action.label === '▶' ? 'Hear it' : notice.action.label}
            style={{
              color: 'var(--nn-text)',
              fontSize: 12,
              // ≥40 px tap target without growing the row
              padding: '10px 6px',
              margin: '-10px -6px',
            }}
          >
            {notice.action.label}
          </UnstyledButton>
        </>
      )}
    </Text>
  ) : job ? (
    <span style={{ display: 'flex', minWidth: 0, maxWidth: '60%', flexShrink: 0 }}>
      <JobPill
        job={job}
        onRetry={canRetryJob ? retryJob : undefined}
        onDismiss={dismissJob}
        stalled={poll.stalled}
      />
    </span>
  ) : null;

  const overflow = (
    <Menu position="bottom-end" width={230} shadow="md" onClose={() => setDeleteArmed(false)}>
      <Menu.Target>
        <UnstyledButton
          aria-label="More"
          style={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--nn-muted)',
          }}
        >
          <IconMore />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item onClick={cmd.loopLine}>
          {state?.loop ? '✓ Loop line (on)' : 'Loop line'}
        </Menu.Item>
        <Menu.Item onClick={cmd.toggleAutoPause}>
          {state?.autoPause ? '✓ Auto-pause (on)' : 'Auto-pause at line end'}
        </Menu.Item>
        <Menu.Item onClick={() => setVocabSpan({ label: video?.title ?? 'Words in this episode' })}>
          Words in this episode
        </Menu.Item>
        <Menu.Item onClick={cmd.markIn}>Mark scene start (i)</Menu.Item>
        <Menu.Item onClick={cmd.markOut}>Mark scene end (o)</Menu.Item>
        {/* M4a marks (D2 / spec § 2): the slider moves the p(known) threshold — "fewer ↔ more"
            is a real statement about the model, not a style. Logging is identical either way. */}
        <Menu.Label>Marks</Menu.Label>
        <div style={{ padding: '2px 12px 10px' }} onClick={(e) => e.stopPropagation()}>
          <Slider
            size="xs"
            min={0}
            max={1}
            step={0.1}
            value={markDensity}
            onChange={(v) => setMarks({ markDensity: v }, false)}
            onChangeEnd={(v) => setMarks({ markDensity: v })}
            disabled={marksHidden || !profile}
            label={(v) => (v <= 0.2 ? 'fewer' : v >= 0.8 ? 'more' : `${Math.round(v * 100)}%`)}
            marks={[
              { value: 0, label: 'fewer' },
              { value: 1, label: 'more' },
            ]}
            styles={{ markLabel: { fontSize: 10, color: 'var(--nn-faint)' } }}
          />
        </div>
        <Menu.Item onClick={() => setMarks({ marksHidden: !marksHidden })} disabled={!profile}>
          {marksHidden ? 'Show marks' : 'Hide marks'}
        </Menu.Item>
        <Menu.Label>Speed</Menu.Label>
        <div style={{ padding: '2px 10px 8px' }}>
          <SegmentedControl
            fullWidth
            size="xs"
            value={String(state?.rate ?? 1)}
            data={rateOptions}
            onChange={(v) => engine?.setRate(Number(v))}
          />
        </div>
        {(canEdit || isMine) && <Menu.Divider />}
        {canEdit && (
          <Menu.Item onClick={cmd.toggleEditor}>
            {editing ? 'Done editing' : 'Edit transcript (e)'}
          </Menu.Item>
        )}
        {isMine && transcriptId && (
          <Menu.Item
            component="a"
            href={api.transcriptExportSrtUrl(transcriptId, {})}
            target="_blank"
            rel="noopener"
          >
            Export SRT
          </Menu.Item>
        )}
        {isMine && (
          <Menu.Item
            closeMenuOnClick={deleteArmed}
            onClick={deleteVideo}
            color={deleteArmed ? 'red' : undefined}
          >
            {deleteLabel}
          </Menu.Item>
        )}
        <Menu.Divider />
        <Menu.Item
          component="a"
          href={`mailto:jeff.haack@gmail.com?subject=${encodeURIComponent(`nelnela: problem with ${title}`)}`}
        >
          Report a problem
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );

  // Portrait editing (F5 mobile subset): the player collapses to its minimum so the keyboard and
  // the row being edited fit; the strip stays.
  const playerCapDvh = !columns && editing ? 30 : 50;
  const player = (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <div
        ref={setBoxEl}
        className="nn-player"
        style={{
          position: 'relative',
          // Stack layout: never taller than half the viewport (short/landscape screens keep the
          // strip and transcript on screen); the box shrinks in width to keep 16:9.
          width: columns ? '100%' : `min(100%, calc(${playerCapDvh}dvh * 16 / 9))`,
          margin: columns ? undefined : '0 auto',
          aspectRatio: '16 / 9',
          background: '#000',
          borderRadius: columns ? 8 : 0,
          overflow: 'hidden',
        }}
      />
      {playerError != null && (
        <Text fz="xs" c="dimmed" px="sm" py={4}>
          {isUpload
            ? uploadPlayerMessage(playerError)
            : playerError === 101 || playerError === 150
              ? "This video can't be embedded — watch it on YouTube."
              : `The player reported an error (${playerError}).`}
        </Text>
      )}
    </div>
  );

  const scenesBar = video && (
    <div
      style={{
        flexShrink: 0,
        // an expanded bar with many scenes scrolls inside itself instead of eating the transcript
        maxHeight: scenesCollapsed ? undefined : columns ? '40vh' : '30dvh',
        overflowY: scenesCollapsed ? undefined : 'auto',
        borderBottom: columns ? undefined : '1px solid var(--nn-hairline)',
      }}
    >
      <ScenesBar
        scenes={scenes}
        durationMs={durationMs}
        currentMs={state?.currentMs ?? 0}
        pendingIn={pendingIn}
        pendingOut={pendingOut}
        loopingSceneId={loopingSceneId}
        entitled={study.entitled}
        collapsed={scenesCollapsed}
        onToggleCollapsed={toggleScenes}
        onSeek={seekMs}
        onMarkIn={markIn}
        onMarkOut={markOut}
        onClearPending={clearPending}
        onAdd={addScene}
        onJump={jumpScene}
        onLoop={loopScene}
        onWords={(sc) => setVocabSpan({ startMs: sc.startMs, endMs: sc.endMs, label: sc.name })}
        onStopLoop={stopLoop}
        onRename={renameScene}
        onDelete={deleteScene}
      />
    </div>
  );

  const transcript = video ? (
    editing && transcriptId ? (
      <TranscriptEditor
        transcriptId={transcriptId}
        video={{
          id: video.id,
          visibility: video.visibility,
          externalId: video.externalId,
          durationMs: video.durationMs,
        }}
        lines={lines}
        revision={revision}
        canEdit={canEdit}
        runningJob={job}
        currentMs={state?.currentMs ?? 0}
        activeIdx={activeIdx}
        onSeek={editorSeek}
        onLinesChanged={onLinesChanged}
        onStartJob={onEditorStartJob}
        onRequestReload={onRequestReload}
        onExit={exitEditor}
        style={{ flex: 1 }}
      />
    ) : (
      <Transcript
        lines={lines}
        activeIdx={activeIdx}
        enMode={enMode}
        revealed={revealed}
        selection={null}
        phrase={phrase}
        marks={marks}
        fontSizePx={transcriptSize}
        onChunkTap={onChunkTap}
        onSeek={cmd.seekLine}
        onWordTap={onWordTap}
        onReveal={onReveal}
        onSwipeStart={onSwipeStart}
        onSwipe={onSwipe}
        onSwipeEnd={onSwipeEnd}
        renderRowEnd={renderRowEnd}
        style={{ flex: 1 }}
      />
    )
  ) : (
    <div style={{ padding: 14 }}>
      {q.isPending ? (
        <>
          <Skeleton height={20} width="60%" mb={10} />
          <Skeleton height={20} width="80%" mb={10} />
          <Skeleton height={20} width="50%" />
        </>
      ) : (
        <Text c="dimmed">
          {q.error instanceof ApiError && q.error.problem.status === 404
            ? 'This video isn’t available.'
            : 'Couldn’t load this video.'}{' '}
          <Link to="/watch" style={{ color: 'var(--nn-link)' }}>
            Back to the library
          </Link>
        </Text>
      )}
    </div>
  );

  const noteBar = noteFor && (
    <LineNoteBar
      key={noteFor.id}
      line={noteFor}
      leftOffset={desktop ? RAIL_WIDTH : 0}
      onSubmit={submitLineNote}
      onCancel={() => setNoteFor(null)}
    />
  );

  if (columns) {
    return (
      <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: 44,
            padding: '0 16px',
            gap: 10,
            borderBottom: '1px solid var(--nn-hairline)',
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <UnstyledButton
            onClick={() => nav('/watch')}
            aria-label="Back to library"
            style={{ display: 'flex', color: 'var(--nn-muted)' }}
          >
            <IconBack />
          </UnstyledButton>
          <Text fz={13.5} fw={600} truncate lang="ka" style={{ minWidth: 0, flexShrink: 1 }}>
            {title}
            {channelName && (
              <span style={{ color: 'var(--nn-muted)', fontWeight: 400 }}> · {channelName}</span>
            )}
          </Text>
          {headerStatus}
          <span style={{ flex: 1 }} />
          {state && (
            <Text fz={11} c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
              {fmtTime(state.currentMs)}
              {video?.durationMs ? ` / ${fmtTime(video.durationMs)}` : ''}
            </Text>
          )}
          <SegmentedControl
            size="xs"
            value={String(state?.rate ?? 1)}
            data={rateOptions}
            onChange={(v) => engine?.setRate(Number(v))}
          />
          {overflow}
        </div>
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <div
            ref={setColEl}
            style={{
              width: wide ? 600 : 'min(560px, 50%)',
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              padding: columns && !desktop ? '8px 0 0 8px' : '14px 0 0 16px',
            }}
          >
            {player}
            {strip}
            {scenesBar}
            <div
              style={{
                fontFamily: 'ui-monospace, Menlo, monospace',
                fontSize: 10,
                lineHeight: 1.6,
                color: 'var(--nn-faint)',
                padding: '6px 2px 0',
                flexShrink: 0,
              }}
            >
              space play · r replay line · ←/→ line · a auto-pause · l loop · h EN mode · i/o mark
              scene{canEdit ? ' · e edit' : ''} · esc close
            </div>
            {desktop && !wide && (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  marginTop: 10,
                  borderTop: '1px solid var(--nn-hairline)',
                }}
              >
                <GlossSheetContent
                  state={sheet}
                  onClose={closeSheet}
                  resume={null}
                  {...sheetActions}
                />
              </div>
            )}
          </div>
          <div
            style={{
              flex: 1,
              minWidth: 0,
              marginLeft: 16,
              borderLeft: '1px solid var(--nn-hairline)',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {transcript}
          </div>
          {wide && (
            <div
              style={{
                width: 320,
                flexShrink: 0,
                borderLeft: '1px solid var(--nn-hairline)',
                background: 'var(--nn-panel)',
                overflowY: 'auto',
              }}
            >
              <GlossSheetContent
                state={sheet}
                onClose={closeSheet}
                resume={null}
                {...sheetActions}
              />
            </div>
          )}
        </div>
        {drawer}
        {vocabDrawer}
        {patternSheet}
        {noteBar}
      </div>
    );
  }

  return (
    <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 44,
          padding: '0 4px',
          gap: 6,
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <UnstyledButton
          onClick={() => nav('/watch')}
          aria-label="Back to library"
          style={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--nn-text)',
            flexShrink: 0,
          }}
        >
          <IconBack />
        </UnstyledButton>
        <Text fz={13.5} fw={600} truncate lang="ka" style={{ minWidth: 0, flexShrink: 1 }}>
          {title}
          {channelName && (
            <span style={{ color: 'var(--nn-muted)', fontWeight: 400 }}> — {channelName}</span>
          )}
        </Text>
        <span style={{ flex: 1 }} />
        {headerStatus}
        {overflow}
      </div>
      {player}
      {strip}
      {scenesBar}
      {transcript}
      {drawer}
      {vocabDrawer}
      {patternSheet}
      {noteBar}
    </div>
  );
}

function StripButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <UnstyledButton
      onClick={onClick}
      aria-label={label}
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        color: 'var(--nn-text)',
        minWidth: 44,
      }}
    >
      {children}
      <span style={{ fontSize: 9.5, color: 'var(--nn-faint)', letterSpacing: '.04em' }}>
        {label}
      </span>
    </UnstyledButton>
  );
}

/**
 * The row-end ＋ (F4/F9a): tap = save the line, long-press (our own 500 ms timer — never the OS
 * callout) = save with a note. A 40×40 target that doesn't grow the 22 px gutter row. Unentitled:
 * faint and inert except for the calm line the parent shows on tap.
 */
function SaveLineButton({
  line,
  entitled,
  onSave,
}: {
  line: Line;
  entitled: boolean;
  onSave: (line: Line, withNote: boolean) => void;
}) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longRef = useRef(false);
  const clear = () => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
  };
  useEffect(() => clear, []);
  return (
    <UnstyledButton
      aria-label="Save line"
      aria-disabled={!entitled}
      title={entitled ? 'Save this line (hold to add a note)' : 'Saving is part of the paid layer'}
      onPointerDown={(e) => {
        if (!e.isPrimary || !entitled) return;
        longRef.current = false;
        clear();
        timerRef.current = setTimeout(() => {
          timerRef.current = null;
          longRef.current = true;
          onSave(line, true);
        }, LONG_PRESS_MS);
      }}
      onPointerUp={clear}
      onPointerLeave={clear}
      onPointerCancel={clear}
      onClick={() => {
        if (longRef.current) {
          longRef.current = false; // the long-press already acted; swallow its trailing click
          return;
        }
        onSave(line, false);
      }}
      style={{
        width: 40,
        height: 40,
        margin: '-9px -8px -9px 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        color: entitled ? 'var(--nn-muted)' : 'var(--nn-faint)',
        opacity: entitled ? 1 : 0.5,
        fontSize: 16,
        lineHeight: 1,
        touchAction: 'manipulation',
      }}
    >
      ＋
    </UnstyledButton>
  );
}

/**
 * Long-press ＋ → "save with a note": one small fixed bar (never a modal) with the note field,
 * sitting where the TTS bar sits — above it while a clip plays.
 */
function LineNoteBar({
  line,
  leftOffset,
  onSubmit,
  onCancel,
}: {
  line: Line;
  leftOffset: number;
  onSubmit: (note: string) => void;
  onCancel: () => void;
}) {
  const [note, setNote] = useState('');
  const tts = useTtsPlayer();
  const bottom = tts.status === 'idle' ? 12 : 12 + 44 + 8;
  const submit = () => {
    const t = note.trim();
    if (t) onSubmit(t);
    else onCancel();
  };
  return (
    <div
      role="dialog"
      aria-label="Save with a note"
      style={{
        position: 'fixed',
        left: leftOffset + 12,
        right: 12,
        bottom: `calc(${bottom}px + env(safe-area-inset-bottom))`,
        margin: '0 auto',
        maxWidth: 560,
        // above the open gloss Drawer (Mantine z 1000): with the click-through overlay a
        // long-press ＋ under an open sheet must show its focused field; the TTS bar stays 190
        zIndex: 1100,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 8px 6px 12px',
        borderRadius: 12,
        background: 'var(--nn-panel-2)',
        border: '1px solid var(--nn-hairline)',
        boxShadow: 'var(--mantine-shadow-md)',
      }}
    >
      <span
        lang="ka"
        style={{
          fontSize: 12,
          color: 'var(--nn-muted)',
          maxWidth: '30%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        {line.text}
      </span>
      <TextInput
        size="sm"
        autoFocus
        aria-label="Note"
        placeholder="A note to yourself — saved with the line"
        value={note}
        maxLength={500}
        onChange={(e) => setNote(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') submit();
          if (e.key === 'Escape') onCancel();
        }}
        style={{ flex: 1, minWidth: 0 }}
      />
      <UnstyledButton
        onClick={submit}
        aria-label="Save with note"
        style={{
          minWidth: 40,
          height: 40,
          padding: '0 8px',
          color: 'var(--nn-text)',
          fontSize: 12.5,
        }}
      >
        Save
      </UnstyledButton>
      <UnstyledButton
        onClick={onCancel}
        aria-label="Cancel"
        style={{ width: 40, height: 40, color: 'var(--nn-faint)' }}
      >
        ✕
      </UnstyledButton>
    </div>
  );
}

/**
 * `playerError` means two different things. For an upload it is a `MediaError.code` (1 aborted,
 * 2 network, 3 decode, 4 source not supported), never a YouTube error code — and the number is
 * useless to the learner, so it is never shown. 3 and 4 are the same problem in practice (this
 * container/codec won't play here) and only a re-encode fixes them; 1 and 2 are transient, so
 * those lines point at trying again rather than at the file.
 */
function uploadPlayerMessage(code: number): string {
  switch (code) {
    case 1:
      return 'Playback stopped. Press play to start it again.';
    case 2:
      return 'The connection dropped while loading this file — try again in a moment.';
    default:
      return 'This file can’t be played here — is it an MP4 (H.264/AAC)?';
  }
}

function glossErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const s = e.problem.status;
    if (s === 429) return 'The AI helper is resting for a bit — cached glosses still work.';
    if (s === 503 || s === 502) return 'The AI helper is unavailable right now.';
    if (s === 422) return 'That word couldn’t be sent for glossing.';
  }
  return 'Couldn’t gloss this word. Try again in a moment.';
}
